// Outbound screening composite (SER-5741): ONE builder node that ships as TWO
// wire nodes — an initiate_call and a contact_screening recipe node joined by
// the `answered` edge. The merge lives entirely in two builder-shaped graph
// passes: expandOutboundScreeningComposites runs before serialization (the
// existing initiate_call and contact_screening paths then do all wire work)
// and collapseOutboundScreeningPairs runs after loading (both halves are
// already builder-formatted when it merges them). No wire-format changes.

import type { Edge, Node } from '@xyflow/react'
import { pick } from 'lodash'
import { v5 } from 'uuid'
import type { EventCallback } from '../components/event-callbacks'
import { FlowNodeTypes, type NodeDataBase } from '../types'

// Expansion ids must be DETERMINISTIC per composite: transformFlowData runs
// twice per unsaved-changes check (current vs original, compared with isEqual)
// and on every stable-id draft save — random ids make composite flows
// permanently dirty and insert a new screening wire node on each save. Never
// change this namespace; doing so re-mints every screening half's identity.
const OUTBOUND_SCREENING_ID_NS = '290a9a4a-cd9f-4170-afd0-deac3a128105'
const screeningHalfId = (compositeId: string) =>
  v5(`${compositeId}:screening`, OUTBOUND_SCREENING_ID_NS)
const answeredEdgeId = (compositeId: string) =>
  v5(`${compositeId}:answered`, OUTBOUND_SCREENING_ID_NS)

/** Map from each composite's minted screening-half WIRE id to the composite's
 * canvas id. Run events reference wire node ids, and only the dial half shares
 * the composite's id — consumers matching run data against the canvas (path
 * highlighting) must remap the screening half through this. */
export function buildScreeningWireIdMap(
  nodes: Array<{ id: string; type?: string }>,
): Map<string, string> {
  const map = new Map<string, string>()
  for (const node of nodes) {
    if (node.type === FlowNodeTypes.outbound_screening) {
      map.set(screeningHalfId(node.id), node.id)
    }
  }
  return map
}

// Carrier outcomes surface on the composite; `answered` is consumed by the
// internal edge. The dormant dial-level `voicemail` port is deliberately
// absent — screening owns voicemail detection.
export const OUTBOUND_SCREENING_CARRIER_PORTS = [
  {
    default: true,
    id: 'no_answer',
    name: 'No answer',
    description: 'The call rang but nobody picked up.',
    section: 'Unanswered',
  },
  {
    default: true,
    id: 'busy_rejected',
    name: 'Busy / Call rejected',
    description: "The call didn't connect: the line was busy or the call was declined.",
    section: 'Unanswered',
  },
  {
    default: true,
    id: 'failed',
    name: 'Call failed',
    description: 'The call could not be placed.',
    section: 'Unanswered',
  },
]
const CARRIER_PORT_IDS = new Set(OUTBOUND_SCREENING_CARRIER_PORTS.map((p) => p.id))

export interface OutboundScreeningConfig {
  name: string
  description?: string | null
  // dial half (initiate_call)
  events?: EventCallback[]
  from?: string
  to: string
  is_validate_caller_id: boolean
  ring_timeout: number
  max_call_duration: number
  // screening half (contact_screening)
  context: string
  ask_permission: boolean
  initial_wait_time?: number
  voicemail_detection_next_node?: string
}

const DIAL_KEYS = [
  'events',
  'from',
  'to',
  'is_validate_caller_id',
  'ring_timeout',
  'max_call_duration',
]
const SCREENING_KEYS = [
  'context',
  'ask_permission',
  'initial_wait_time',
  'voicemail_detection_next_node',
  'leader_profile',
  'screening_task',
  'intents',
]

type BuilderNode = Node<NodeDataBase<unknown>>

// The dial half needs its own unique node name (backend rejects duplicates);
// the screening half keeps the composite's name so downstream templates read
// {{<composite name>.screening_*}} exactly as users expect.
const dialName = (name: string) => `${name} · Dial`

/** Builder-side pre-pass for transformFlowData: replace every composite node
 * with its initiate_call + contact_screening pair and the internal edge. */
export function expandOutboundScreeningComposites(
  nodes: BuilderNode[],
  edges: Edge[],
): { nodes: BuilderNode[]; edges: Edge[] } {
  if (!nodes.some((n) => n.type === FlowNodeTypes.outbound_screening)) {
    return { nodes, edges }
  }
  const outNodes: BuilderNode[] = []
  const internalEdges: Edge[] = []
  // composite id -> minted screening id; edges are rewritten in one pure map
  // at the end — the canvas owns the input objects, never mutate them.
  const screeningIdByComposite = new Map<string, string>()

  for (const node of nodes) {
    if (node.type !== FlowNodeTypes.outbound_screening) {
      outNodes.push(node)
      continue
    }
    const cfg = node.data.config as unknown as OutboundScreeningConfig
    const screeningId = screeningHalfId(node.id)
    screeningIdByComposite.set(node.id, screeningId)

    outNodes.push({
      ...node,
      type: FlowNodeTypes.initiate_call,
      data: {
        ...node.data,
        meta: { ...node.data.meta, name: 'Initiate call' },
        config: {
          ...pick(cfg as unknown as Record<string, unknown>, DIAL_KEYS),
          name: dialName(cfg.name),
          description: 'Dial half of the outbound screening node.',
          detect_voicemail: false,
          send_digits: '',
          extra_headers: '',
          record: null,
          events: cfg.events ?? [],
        },
      },
    } as BuilderNode)

    outNodes.push({
      id: screeningId,
      type: FlowNodeTypes.contact_screening,
      position: { x: node.position.x, y: node.position.y + 160 },
      data: {
        meta: { name: 'Screening Contact' },
        config: {
          ...pick(cfg as unknown as Record<string, unknown>, SCREENING_KEYS),
          name: cfg.name,
          description: cfg.description ?? '',
        },
      },
    } as BuilderNode)

    internalEdges.push({
      id: answeredEdgeId(node.id),
      source: node.id,
      target: screeningId,
      sourceHandle: 'answered',
    } as Edge)
  }

  // Screening-outcome edges re-source to the screening half; carrier edges
  // and edges INTO the composite already reference the dial half's id.
  const outEdges = edges.map((e) => {
    const screeningId = screeningIdByComposite.get(e.source)
    return screeningId &&
      e.sourceHandle &&
      e.sourceHandle !== 'answered' &&
      !CARRIER_PORT_IDS.has(e.sourceHandle)
      ? { ...e, source: screeningId }
      : e
  })
  return { nodes: outNodes, edges: [...outEdges, ...internalEdges] }
}

/** Builder-side post-pass for inverseTransformFlowData: fold every
 * initiate_call → answered → contact_screening pair back into one composite.
 * Runs after the existing machinery, so both halves are already formatted
 * (minutes conversion, screening flatten/retype) — the merge is a spread. */
export function collapseOutboundScreeningPairs(
  nodes: BuilderNode[],
  edges: Edge[],
): { nodes: BuilderNode[]; edges: Edge[] } {
  const byId = new Map(nodes.map((n) => [n.id, n]))
  const collapsed = new Set<string>()
  const dropEdges = new Set<string>()
  const outNodes: BuilderNode[] = []
  // screening id -> composite (call) id; edges rewritten in one pure map below
  const compositeByScreening = new Map<string, string>()

  for (const call of nodes) {
    if (call.type !== FlowNodeTypes.initiate_call) continue
    const answered = edges.filter((e) => e.source === call.id && e.sourceHandle === 'answered')
    if (answered.length !== 1) continue
    const screening = byId.get(answered[0].target)
    if (!screening || screening.type !== FlowNodeTypes.contact_screening) continue
    // only a clean pair collapses: the answered edge must be the screening
    // node's sole inbound
    const inbound = edges.filter((e) => e.target === screening.id)
    if (inbound.length !== 1) continue

    const callCfg = call.data.config as Record<string, unknown>
    const scrCfg = screening.data.config as Record<string, unknown>
    outNodes.push({
      ...call,
      type: FlowNodeTypes.outbound_screening,
      data: {
        meta: { name: 'Initiate call and screen' },
        config: {
          ...pick(callCfg, DIAL_KEYS),
          ...pick(scrCfg, SCREENING_KEYS),
          name: (scrCfg.name as string) || (callCfg.name as string),
          description: (scrCfg.description as string) ?? '',
        },
      },
    } as BuilderNode)
    collapsed.add(call.id)
    collapsed.add(screening.id)
    dropEdges.add(answered[0].id)
    compositeByScreening.set(screening.id, call.id)
  }

  if (collapsed.size === 0) return { nodes, edges }
  return {
    nodes: [...outNodes, ...nodes.filter((n) => !collapsed.has(n.id))],
    // screening-outcome edges re-source to the composite (== the call id)
    edges: edges
      .filter((e) => !dropEdges.has(e.id))
      .map((e) => {
        const compositeId = compositeByScreening.get(e.source)
        return compositeId ? { ...e, source: compositeId } : e
      }),
  }
}
